export const RX_PRINT_COLOR_STYLE_KEYS = {
  headerBackground: 'rxTableHeaderBackgroundColor',
  headerText: 'rxTableHeaderTextColor',
  border: 'rxTableBorderColor',
  rowBackground: 'rxTableRowBackgroundColor',
  alternateRowBackground: 'rxTableAlternateRowBackgroundColor',
  medicineText: 'rxMedicineTextColor',
  doseText: 'rxDoseTextColor',
  whenText: 'rxWhenTextColor',
  frequencyText: 'rxFrequencyTextColor',
  durationText: 'rxDurationTextColor',
  notesText: 'rxNotesTextColor',
  notesBackground: 'rxNotesBackgroundColor',
  genericText: 'rxGenericTextColor',
  genericBackground: 'rxGenericBackgroundColor',
}

// Defaults darkened for stronger contrast / readability: body values are near-black
// instead of muted slate/blue/purple, while the (purple) table header is kept as an
// accent. These are only fallbacks — any per-doctor colour override still wins.
export const DEFAULT_RX_PRINT_COLOR_STYLES = {
  [RX_PRINT_COLOR_STYLE_KEYS.headerBackground]: '#eef2ff',
  [RX_PRINT_COLOR_STYLE_KEYS.headerText]: '#3730a3',
  [RX_PRINT_COLOR_STYLE_KEYS.border]: '#b4bdf0',
  [RX_PRINT_COLOR_STYLE_KEYS.rowBackground]: '#ffffff',
  [RX_PRINT_COLOR_STYLE_KEYS.alternateRowBackground]: '#f5f7ff',
  [RX_PRINT_COLOR_STYLE_KEYS.medicineText]: '#0f172a',
  [RX_PRINT_COLOR_STYLE_KEYS.doseText]: '#111827',
  [RX_PRINT_COLOR_STYLE_KEYS.whenText]: '#111827',
  [RX_PRINT_COLOR_STYLE_KEYS.frequencyText]: '#111827',
  [RX_PRINT_COLOR_STYLE_KEYS.durationText]: '#111827',
  [RX_PRINT_COLOR_STYLE_KEYS.notesText]: '#1f2937',
  [RX_PRINT_COLOR_STYLE_KEYS.notesBackground]: '#f7f5ff',
  [RX_PRINT_COLOR_STYLE_KEYS.genericText]: '#374151',
  [RX_PRINT_COLOR_STYLE_KEYS.genericBackground]: '#ede9fe',
}

export const RX_PRINT_COLOR_STYLE_FIELDS = [
  {
    key: RX_PRINT_COLOR_STYLE_KEYS.headerBackground,
    label: 'Table header background',
  },
  {
    key: RX_PRINT_COLOR_STYLE_KEYS.headerText,
    label: 'Table header text',
  },
  {
    key: RX_PRINT_COLOR_STYLE_KEYS.border,
    label: 'Table borders',
  },
  {
    key: RX_PRINT_COLOR_STYLE_KEYS.rowBackground,
    label: 'Row background',
  },
  {
    key: RX_PRINT_COLOR_STYLE_KEYS.alternateRowBackground,
    label: 'Alternate row background',
  },
  {
    key: RX_PRINT_COLOR_STYLE_KEYS.medicineText,
    label: 'Medicine text',
  },
  {
    key: RX_PRINT_COLOR_STYLE_KEYS.doseText,
    label: 'Dose text',
  },
  {
    key: RX_PRINT_COLOR_STYLE_KEYS.whenText,
    label: 'When text',
  },
  {
    key: RX_PRINT_COLOR_STYLE_KEYS.frequencyText,
    label: 'Frequency text',
  },
  {
    key: RX_PRINT_COLOR_STYLE_KEYS.durationText,
    label: 'Duration text',
  },
  {
    key: RX_PRINT_COLOR_STYLE_KEYS.notesText,
    label: 'Notes text',
  },
  {
    key: RX_PRINT_COLOR_STYLE_KEYS.notesBackground,
    label: 'Notes background',
  },
  {
    key: RX_PRINT_COLOR_STYLE_KEYS.genericText,
    label: 'Generic name text',
  },
  {
    key: RX_PRINT_COLOR_STYLE_KEYS.genericBackground,
    label: 'Generic name background',
  },
]

const BW_RX_PRINT_COLOR_STYLES = {
  [RX_PRINT_COLOR_STYLE_KEYS.headerBackground]: '#e8e8e8',
  [RX_PRINT_COLOR_STYLE_KEYS.headerText]: '#000000',
  [RX_PRINT_COLOR_STYLE_KEYS.border]: '#8f8f8f',
  [RX_PRINT_COLOR_STYLE_KEYS.rowBackground]: '#ffffff',
  [RX_PRINT_COLOR_STYLE_KEYS.alternateRowBackground]: '#f5f5f5',
  [RX_PRINT_COLOR_STYLE_KEYS.medicineText]: '#111111',
  [RX_PRINT_COLOR_STYLE_KEYS.doseText]: '#1f1f1f',
  [RX_PRINT_COLOR_STYLE_KEYS.whenText]: '#1f1f1f',
  [RX_PRINT_COLOR_STYLE_KEYS.frequencyText]: '#111111',
  [RX_PRINT_COLOR_STYLE_KEYS.durationText]: '#1f1f1f',
  [RX_PRINT_COLOR_STYLE_KEYS.notesText]: '#3f3f3f',
  [RX_PRINT_COLOR_STYLE_KEYS.notesBackground]: '#f9f9f9',
  [RX_PRINT_COLOR_STYLE_KEYS.genericText]: '#4b4b4b',
  [RX_PRINT_COLOR_STYLE_KEYS.genericBackground]: '#eeeeee',
}

function isColorLike(value) {
  if (typeof value !== 'string') return false
  return (
    /^#[0-9a-f]{3}([0-9a-f]{3})?$/i.test(value.trim()) ||
    /^rgba?\(/i.test(value.trim()) ||
    /^hsla?\(/i.test(value.trim())
  )
}

function getColorFromOtherStyles(otherStyles, key, fallback) {
  const value = otherStyles?.[key]
  return isColorLike(value) ? value.trim() : fallback
}

export function resolveDrugAdvicePrintColorStyles(styleOverrides = {}, colorMode = 'colored') {
  if (colorMode === 'bw') {
    return {
      headerBackground: BW_RX_PRINT_COLOR_STYLES[RX_PRINT_COLOR_STYLE_KEYS.headerBackground],
      headerText: BW_RX_PRINT_COLOR_STYLES[RX_PRINT_COLOR_STYLE_KEYS.headerText],
      border: BW_RX_PRINT_COLOR_STYLES[RX_PRINT_COLOR_STYLE_KEYS.border],
      rowBackground: BW_RX_PRINT_COLOR_STYLES[RX_PRINT_COLOR_STYLE_KEYS.rowBackground],
      alternateRowBackground: BW_RX_PRINT_COLOR_STYLES[RX_PRINT_COLOR_STYLE_KEYS.alternateRowBackground],
      medicineText: BW_RX_PRINT_COLOR_STYLES[RX_PRINT_COLOR_STYLE_KEYS.medicineText],
      doseText: BW_RX_PRINT_COLOR_STYLES[RX_PRINT_COLOR_STYLE_KEYS.doseText],
      whenText: BW_RX_PRINT_COLOR_STYLES[RX_PRINT_COLOR_STYLE_KEYS.whenText],
      frequencyText: BW_RX_PRINT_COLOR_STYLES[RX_PRINT_COLOR_STYLE_KEYS.frequencyText],
      durationText: BW_RX_PRINT_COLOR_STYLES[RX_PRINT_COLOR_STYLE_KEYS.durationText],
      notesText: BW_RX_PRINT_COLOR_STYLES[RX_PRINT_COLOR_STYLE_KEYS.notesText],
      notesBackground: BW_RX_PRINT_COLOR_STYLES[RX_PRINT_COLOR_STYLE_KEYS.notesBackground],
      genericText: BW_RX_PRINT_COLOR_STYLES[RX_PRINT_COLOR_STYLE_KEYS.genericText],
      genericBackground: BW_RX_PRINT_COLOR_STYLES[RX_PRINT_COLOR_STYLE_KEYS.genericBackground],
    }
  }

  const otherStyles = styleOverrides?.otherStyles || {}
  const sectionHeadingColor = styleOverrides?.headingColor
  const sectionTextColor = styleOverrides?.color

  const bodyText =
    getColorFromOtherStyles(
      otherStyles,
      'rxTableBodyTextColor',
      sectionTextColor || DEFAULT_RX_PRINT_COLOR_STYLES[RX_PRINT_COLOR_STYLE_KEYS.doseText],
    )

  return {
    headerBackground: getColorFromOtherStyles(
      otherStyles,
      RX_PRINT_COLOR_STYLE_KEYS.headerBackground,
      DEFAULT_RX_PRINT_COLOR_STYLES[RX_PRINT_COLOR_STYLE_KEYS.headerBackground],
    ),
    headerText: getColorFromOtherStyles(
      otherStyles,
      RX_PRINT_COLOR_STYLE_KEYS.headerText,
      sectionHeadingColor || DEFAULT_RX_PRINT_COLOR_STYLES[RX_PRINT_COLOR_STYLE_KEYS.headerText],
    ),
    border: getColorFromOtherStyles(
      otherStyles,
      RX_PRINT_COLOR_STYLE_KEYS.border,
      DEFAULT_RX_PRINT_COLOR_STYLES[RX_PRINT_COLOR_STYLE_KEYS.border],
    ),
    rowBackground: getColorFromOtherStyles(
      otherStyles,
      RX_PRINT_COLOR_STYLE_KEYS.rowBackground,
      DEFAULT_RX_PRINT_COLOR_STYLES[RX_PRINT_COLOR_STYLE_KEYS.rowBackground],
    ),
    alternateRowBackground: getColorFromOtherStyles(
      otherStyles,
      RX_PRINT_COLOR_STYLE_KEYS.alternateRowBackground,
      DEFAULT_RX_PRINT_COLOR_STYLES[RX_PRINT_COLOR_STYLE_KEYS.alternateRowBackground],
    ),
    medicineText: getColorFromOtherStyles(
      otherStyles,
      RX_PRINT_COLOR_STYLE_KEYS.medicineText,
      sectionTextColor || DEFAULT_RX_PRINT_COLOR_STYLES[RX_PRINT_COLOR_STYLE_KEYS.medicineText],
    ),
    doseText: getColorFromOtherStyles(
      otherStyles,
      RX_PRINT_COLOR_STYLE_KEYS.doseText,
      bodyText,
    ),
    whenText: getColorFromOtherStyles(
      otherStyles,
      RX_PRINT_COLOR_STYLE_KEYS.whenText,
      bodyText,
    ),
    frequencyText: getColorFromOtherStyles(
      otherStyles,
      RX_PRINT_COLOR_STYLE_KEYS.frequencyText,
      DEFAULT_RX_PRINT_COLOR_STYLES[RX_PRINT_COLOR_STYLE_KEYS.frequencyText],
    ),
    durationText: getColorFromOtherStyles(
      otherStyles,
      RX_PRINT_COLOR_STYLE_KEYS.durationText,
      bodyText,
    ),
    notesText: getColorFromOtherStyles(
      otherStyles,
      RX_PRINT_COLOR_STYLE_KEYS.notesText,
      DEFAULT_RX_PRINT_COLOR_STYLES[RX_PRINT_COLOR_STYLE_KEYS.notesText],
    ),
    notesBackground: getColorFromOtherStyles(
      otherStyles,
      RX_PRINT_COLOR_STYLE_KEYS.notesBackground,
      DEFAULT_RX_PRINT_COLOR_STYLES[RX_PRINT_COLOR_STYLE_KEYS.notesBackground],
    ),
    genericText: getColorFromOtherStyles(
      otherStyles,
      RX_PRINT_COLOR_STYLE_KEYS.genericText,
      DEFAULT_RX_PRINT_COLOR_STYLES[RX_PRINT_COLOR_STYLE_KEYS.genericText],
    ),
    genericBackground: getColorFromOtherStyles(
      otherStyles,
      RX_PRINT_COLOR_STYLE_KEYS.genericBackground,
      DEFAULT_RX_PRINT_COLOR_STYLES[RX_PRINT_COLOR_STYLE_KEYS.genericBackground],
    ),
  }
}
